/**
 * Konten dalam kata layar: keadaannya, platformnya, asalnya, dan jamnya. Masukannya konten
 * GABUNGAN dari `gabungKonten` (satu konten, `tujuan` berisi baris per platform).
 */
import { hariIni, hariTanggalPendek, jam } from '../../shared/lib/waktu'
import { kontenMenunggu } from './antrean'

const URUT_PLATFORM = ['instagram', 'facebook', 'linkedin', 'tiktok']
/** Urutan platform di layar: Instagram dulu, lalu Facebook. */
export const urutPlatform = (a, b) => URUT_PLATFORM.indexOf(a.platform) - URUT_PLATFORM.indexOf(b.platform)

/** `{ kode, nada }`. `kode` adalah kunci kamus `k6.status.<kode>`. */
export function keadaanKonten(k) {
  switch (k.status) {
    case 'pending_concept': return { kode: 'konsep', nada: 'warn' }
    // Konten otomatis sudah punya jam usulan dan tinggal disetujui; buatan orang belum.
    case 'pending_approval': return { kode: k.scheduled_at ? 'menunggu' : 'siap', nada: 'warn' }
    case 'failed': return { kode: 'gagal', nada: 'bad' }
    case 'draft': return { kode: k.error ? 'gambarGagal' : 'draf', nada: 'warn' }
    case 'scheduled': return { kode: 'terjadwal', nada: 'aksen' }
    case 'published': return { kode: 'terbit', nada: 'ok' }
    default: return { kode: 'ditolak', nada: 'idle' }
  }
}

/** Baris yang masih dipakai konten ini, urut platform. Kalau semuanya ditolak, semuanya. */
export function tujuanDipakai(k) {
  const hidup = k.tujuan.filter((r) => r.status !== 'rejected')
  return [...(hidup.length ? hidup : k.tujuan)].sort(urutPlatform)
}

/** Platform yang dilepas untuk konten ini ("tidak dipakai"), selagi yang lain jalan. */
export function tujuanDilepas(k) {
  const hidup = k.tujuan.some((r) => r.status !== 'rejected')
  return hidup ? k.tujuan.filter((r) => r.status === 'rejected').sort(urutPlatform) : []
}

const BELUM_TERBIT = ['pending_concept', 'pending_approval', 'draft', 'failed']
const terjadwalNanti = (r, kini) => r.status === 'scheduled' && !!r.scheduled_at && new Date(r.scheduled_at) > kini

/**
 * Teks dan gambar baris ini masih boleh diubah: belum terbit, dan yang TERJADWAL sampai jam
 * terbitnya (keputusan operator 7 Okt). Sama dengan `_boleh_disunting` di backend; begitu
 * jamnya tiba barisnya milik penerbit.
 */
export function barisBisaDiubah(r, kini = new Date()) {
  return BELUM_TERBIT.includes(r.status) || terjadwalNanti(r, kini)
}

/**
 * Cara sebuah konten ditambahi platform, sama dengan `keputusan.tambah_platform` di backend:
 *
 *   'ikut'    masih ada barisnya yang draf, menunggu, atau terjadwal dan jamnya belum tiba;
 *             platform barunya meniru baris itu, dan tidak ada yang terbit dari tombolnya
 *   'terbit'  semuanya sudah tayang; platform barunya DITERBITKAN SEKARANG (8 Okt)
 *   null      tidak bisa: gagal terbit, ditolak, atau jam terbitnya sudah tiba
 */
export function caraTambahPlatform(k, kini = new Date()) {
  if (k.tujuan.some((r) => r.status === 'draft' || r.status === 'pending_approval' || terjadwalNanti(r, kini))) return 'ikut'
  return k.tujuan.some((r) => r.status === 'published') ? 'terbit' : null
}

/** Platform yang siap untuk tenant ini (`GET /dashboard/tujuan`) dan belum dipakai konten ini. */
export function platformBisaDitambah(k, tujuan) {
  const dipakai = new Set(k.tujuan.filter((r) => r.status !== 'rejected').map((r) => r.platform))
  return (tujuan ?? []).filter((x) => x.siap && !dipakai.has(x.platform)).sort(urutPlatform)
}

/** Baris yang gagal terbit, urut platform. */
export const tujuanGagal = (k) => k.tujuan.filter((r) => r.status === 'failed').sort(urutPlatform)

/** Gambar pertama konten ini, atau null. */
export const gambarKonten = (k) => k.media_url || k.tujuan.find((r) => r.media_url)?.media_url || null

const ARAH_KONSEP = ['umum', 'produk', 'iklan']

/**
 * Arah konsepnya (ADR-108): `umum`, `produk`, atau `iklan`. Null untuk konten yang tidak pernah
 * memilih arah: konsep dari sebelum arah ada, dan konten yang ditulis tangan di Buat konten.
 */
export const arahKonten = (k) => (ARAH_KONSEP.includes(k.arah) ? k.arah : null)

/** Dokumen Pengetahuan yang dipakai menulis konsepnya. Kosong untuk arah `umum`. */
export const pengetahuanKonten = (k) => (['produk', 'iklan'].includes(arahKonten(k)) ? (k.pengetahuan ?? []).filter(Boolean) : [])

/**
 * Dari mana konten ini: rantai otomatis, atau dibuat orang lewat Telegram atau Smova. Arah
 * konsepnya ikut disebut kalau ada, supaya terbaca di daftar tanpa membuka kontennya.
 */
export function asalKonten(k, t) {
  const asal = k.jalur === 'otomatis'
    ? t('k6.asal.otomatis')
    : t(String(k.asal ?? '').startsWith('telegram') ? 'k6.asal.telegram' : 'k6.asal.smova')
  const arah = arahKonten(k)
  return arah ? `${asal} · ${t(`p3.arah.${arah}.judul`)}` : asal
}

/** "Hari ini 16.00" atau "Sel, 6 Okt 09.00". */
export function labelJam(nilai, t, locale, kini = new Date()) {
  const hari = hariIni(nilai, kini) ? t('prospek.hariIni') : hariTanggalPendek(nilai, locale)
  return `${hari} ${jam(nilai, locale)}`
}

const waktu = (v) => (v ? new Date(v).getTime() : Number.POSITIVE_INFINITY)

// Yang gagal terbit paling atas: itu yang paling lama dibiarkan kalau tenggelam. Lalu yang
// belum berjam (menunggu orangnya memilih), lalu menurut jamnya.
const urutMenunggu = (k) => (k.status === 'failed' ? 0 : k.scheduled_at ? 2 : 1)

/**
 * Pilihan urutan tiap tab daftar konten; yang PERTAMA bawaannya (permintaan operator 11 Okt).
 * Menunggu diurut menurut kapan kontennya dibuat, Terjadwal menurut jam terbitnya, dan Sudah
 * tayang menurut jam tayangnya.
 *
 * Menunggu Anda dibuka dari yang TERBARU dibuat (operator, 12 Okt: "langsung dari urutan yang
 * terbaru"). "Paling mendesak", bawaan sampai hari itu, tetap bisa dipilih: yang gagal terbit
 * dulu, lalu yang belum berjam, lalu menurut jamnya.
 */
export const URUTAN_KONTEN = {
  menunggu: ['terbaru', 'terlama', 'mendesak'],
  terjadwal: ['terdekat', 'terjauh'],
  tayang: ['terbaru', 'terlama'],
}

/** Urutan yang berlaku untuk tab itu: yang tidak dikenal jatuh ke bawaannya. */
export const urutanSah = (tab, nilai) => (URUTAN_KONTEN[tab].includes(nilai) ? nilai : URUTAN_KONTEN[tab][0])

const dibuat = (k) => (k.created_at ? new Date(k.created_at).getTime() : 0)
const tayangPada = (k) => waktu(k.published_at ?? k.scheduled_at)

/**
 * Tiga tab daftar konten. Tanpa pilihan: Menunggu yang terbaru dibuat dulu, Terjadwal yang
 * paling dekat dulu, Sudah tayang yang terbaru dulu. `urut` berisi pilihan orangnya per tab.
 */
export function pilahKonten(gabungan, urut = {}) {
  const m = urutanSah('menunggu', urut.menunggu)
  const j = urutanSah('terjadwal', urut.terjadwal)
  const y = urutanSah('tayang', urut.tayang)
  return {
    menunggu: kontenMenunggu(gabungan).sort((a, b) => {
      if (m === 'terbaru') return dibuat(b) - dibuat(a)
      if (m === 'terlama') return dibuat(a) - dibuat(b)
      return urutMenunggu(a) - urutMenunggu(b) || waktu(a.scheduled_at) - waktu(b.scheduled_at)
    }),
    terjadwal: gabungan.filter((k) => k.status === 'scheduled')
      .sort((a, b) => (j === 'terjauh' ? waktu(b.scheduled_at) - waktu(a.scheduled_at) : waktu(a.scheduled_at) - waktu(b.scheduled_at))),
    tayang: gabungan.filter((k) => k.status === 'published')
      .sort((a, b) => (y === 'terlama' ? tayangPada(a) - tayangPada(b) : tayangPada(b) - tayangPada(a))),
  }
}

/** "Instagram dan Facebook": daftar nama untuk kalimat. */
export function daftarKalimat(nama, t) {
  if (nama.length <= 1) return nama.join('')
  return `${nama.slice(0, -1).join(', ')} ${t('umum.dan')} ${nama.at(-1)}`
}

/**
 * Galat penerbitan dalam bahasa orang.
 *
 * Yang tersimpan di `error` jawaban mentah Meta — JSON lengkap dengan `fbtrace_id`.
 * `(#100) url should represent a valid URL` tidak memberi tahu siapa pun apa yang harus
 * dilakukan, dan yang membacanya menyimpulkan "sistemnya rusak" lalu berhenti di situ.
 *
 * Dicocokkan lewat POTONGAN TEKS, bukan kode galat saja: Meta memakai `code: 100` untuk
 * banyak hal yang berbeda, dan menerjemahkan seluruh 100 jadi satu kalimat akan salah
 * lebih sering daripada benar.
 *
 * Yang tidak dikenali dikembalikan APA ADANYA, bukan diganti "terjadi kesalahan". Galat
 * mentah yang jelek tetap lebih berguna daripada kalimat sopan yang tidak menyebut apa pun
 * — dan ia satu-satunya petunjuk yang tersisa saat sesuatu yang baru rusak.
 */
export function jelaskanGagal(mentah, t) {
  const x = String(mentah || '')
  if (!x.trim()) return null
  if (x.includes('url should represent a valid URL')) return t('konten.galatUrl')
  // Belum pernah tersambung, atau sambungannya dimatikan: bukan token yang kedaluwarsa, dan
  // yang bisa membereskannya Tenant Admin di Sambungan.
  if (/tidak punya .*access_token|belum tersambung|AkunTidakTerdaftar/i.test(x)) return t('konten.galatSambungan')
  if (/access token|OAuthException.*190|Session has expired/i.test(x)) return t('konten.galatToken')
  if (/rate limit|too many calls|#4\b/i.test(x)) return t('konten.galatKuota')
  // IZIN MEMBACA DIPISAH DARI IZIN MENERBITKAN, dan bedanya penting bagi yang
  // membacanya: yang ini TIDAK menghentikan penerbitan sama sekali — kontennya
  // tetap tayang, cuma angkanya yang tidak bisa diambil. Satu kalimat
  // "belum punya izin menerbitkan" untuk keduanya akan membuat orang mengira
  // penerbitannya ikut mati, dan itu kabar buruk yang salah.
  if (/pages_read_user_content|Page Public Content Access/i.test(x)) {
    return t('konten.galatIzinBaca')
  }
  if (/permission|#200\b/i.test(x)) return t('konten.galatIzin')
  if (/media|image|aspect ratio|unsupported/i.test(x)) return t('konten.galatMedia')
  return x.slice(0, 160)
}

/**
 * Satu baris ketikan -> daftar hashtag yang bersih.
 *
 * `#` DIBUANG DI SATU TEMPAT. `build_caption` di backend menambahkannya sendiri saat
 * merakit caption; menyimpannya dengan pagar menghasilkan `##asuransi` di Instagram —
 * dan itu bukan cuma jelek, tagar ganda tidak terhubung ke tagar mana pun.
 *
 * Dipisah dengan spasi ATAU koma: orang mengetik keduanya, dan yang cuma menerima satu
 * bentuk menghasilkan satu hashtag panjang berisi seluruh ketikannya.
 *
 * Yang kembar dibuang, tanpa peduli besar-kecil hurufnya — Instagram menganggap
 * `#Asuransi` dan `#asuransi` tagar yang sama, jadi keduanya cuma memakai jatah.
 */
export function bacaHashtag(teks) {
  const terlihat = new Set()
  const hasil = []
  for (const potong of String(teks || '').split(/[\s,]+/)) {
    const t = potong.replace(/^#+/, '').trim()
    if (!t || terlihat.has(t.toLowerCase())) continue
    terlihat.add(t.toLowerCase())
    hasil.push(t)
  }
  return hasil
}

/**
 * Nama berkas dokumen Pengetahuan sebagai sebutan produknya: tanpa ekstensi, garis bawah dan
 * tanda hubung jadi spasi. Cermin `nama_produk` di `app/domains/content/arah.py`.
 */
export function namaProduk(berkas) {
  const b = String(berkas ?? '')
  const inti = b.includes('.') ? b.slice(0, b.lastIndexOf('.')) : b
  return inti.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim() || b
}
